import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import type { TablePaginationConfig } from "antd";
import type { FilterValue } from "antd/es/table/interface";

export const DEFAULT_PAGE_SIZE = 20;
const PREFETCH_COUNT = 5;
export const FETCH_PAGE_SIZE = DEFAULT_PAGE_SIZE * PREFETCH_COUNT;

interface CursorPage<T> {
  data: T[];
  page_size: number;
}

interface UseCursorPaginationOptions<T> {
  pages: CursorPage<T>[] | undefined;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  pageSize?: number;
  // When provided, the current page is persisted in this URL search param
  // instead of local state, so it survives navigating away (e.g. to a
  // details page) and back.
  paramKey?: string;
}

export function useCursorPagination<T>({
  pages,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  pageSize = DEFAULT_PAGE_SIZE,
  paramKey,
}: UseCursorPaginationOptions<T>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localPage, setLocalPage] = useState(1);
  const [isFiltered, setIsFiltered] = useState(false);

  const pageFromUrl = paramKey ? Number(searchParams.get(paramKey)) : NaN;
  const currentPage = paramKey && pageFromUrl > 0 ? pageFromUrl : localPage;

  const setCurrentPage = useCallback(
    (page: number) => {
      if (paramKey) {
        const next = new URLSearchParams(searchParams);
        next.set(paramKey, String(page));
        setSearchParams(next, { replace: true });
      } else {
        setLocalPage(page);
      }
    },
    [paramKey, searchParams, setSearchParams],
  );

  // Flatten all API pages into a single list — let Ant Design Table handle
  // client-side pagination so that column filters apply across ALL data.
  const allItems = useMemo(() => pages?.flatMap((p) => p.data) ?? [], [pages]);

  const displayPageCount = Math.ceil(allItems.length / pageSize) || 1;

  // When filtered, omit total so Ant Design derives it from the filtered data.
  // When unfiltered, add a phantom entry so the user sees there's a next page.
  const total = isFiltered
    ? undefined
    : allItems.length + (hasNextPage ? 1 : 0);

  // Prefetch next API page when the user is near the end
  useEffect(() => {
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      currentPage >= displayPageCount - 1
    ) {
      fetchNextPage();
    }
  }, [
    currentPage,
    displayPageCount,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const isPageLoading =
    !isFiltered && currentPage > displayPageCount && isFetchingNextPage;

  const pagination: TablePaginationConfig = {
    current: currentPage,
    pageSize,
    total,
    onChange: handlePageChange,
    showSizeChanger: false,
  };

  // Callback for Table's onChange — tracks filter state and resets page when
  // filters change. Tables with column filters should pass this as onChange.
  const onTableChange = useCallback(
    (
      pag: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
    ) => {
      const hasActiveFilters = Object.values(filters).some(
        (v) => v !== null && v.length > 0,
      );
      setIsFiltered(hasActiveFilters);

      if (pag.current) {
        setCurrentPage(pag.current);
      }
    },
    [setCurrentPage],
  );

  return { dataSource: allItems, pagination, isPageLoading, onTableChange };
}
